import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface Node {
  id: number;
  x: number;
  y: number;
  layer: number;
}

interface Connection {
  from: number;
  to: number;
  active: boolean;
}

interface NeuralNetworkProps {
  className?: string;
  width?: number;
  height?: number;
}

export const NeuralNetwork: React.FC<NeuralNetworkProps> = ({
  className = '',
  width = 400,
  height = 300,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<Node[]>([]);
  const connectionsRef = useRef<Connection[]>([]);
  const signalsRef = useRef<{ connection: number; progress: number }[]>([]);
  const reducedMotion = useReducedMotion();
  const [isInView, setIsInView] = useState(true);

  // Initialize network structure
  const initNetwork = useCallback(() => {
    const nodes: Node[] = [];
    const connections: Connection[] = [];
    
    // Create layers: 3-4-4-3
    const layers = [3, 4, 4, 3];
    const useAdaptiveLayout = width < 350;
    const layerX = useAdaptiveLayout
      ? (() => {
          const horizontalPadding = Math.max(20, Math.round(width * 0.12));
          const usableWidth = Math.max(width - horizontalPadding * 2, 80);
          return layers.map(
            (_count, index) =>
              horizontalPadding + (usableWidth * index) / Math.max(layers.length - 1, 1)
          );
        })()
      : [50, 150, 250, 350];
    
    let nodeId = 0;
    layers.forEach((count, layerIndex) => {
      const spacing = height / (count + 1);
      for (let i = 0; i < count; i++) {
        nodes.push({
          id: nodeId++,
          x: layerX[layerIndex],
          y: spacing * (i + 1),
          layer: layerIndex,
        });
      }
    });
    
    // Create connections between layers
    nodes.forEach((node) => {
      if (node.layer < layers.length - 1) {
        nodes
          .filter((n) => n.layer === node.layer + 1)
          .forEach((target) => {
            connections.push({
              from: node.id,
              to: target.id,
              active: Math.random() > 0.5,
            });
          });
      }
    });
    
    nodesRef.current = nodes;
    connectionsRef.current = connections;
  }, [height, width]);

  useEffect(() => {
    initNetwork();
  }, [initNetwork]);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.05, rootMargin: '120px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reducedMotion || !isInView) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    let lastSignalTime = 0;

    const animate = (timestamp: number) => {
      ctx.clearRect(0, 0, width, height);
      
      const nodes = nodesRef.current;
      const connections = connectionsRef.current;
      
      // Draw connections
      connections.forEach((conn) => {
        const fromNode = nodes.find((n) => n.id === conn.from);
        const toNode = nodes.find((n) => n.id === conn.to);
        if (!fromNode || !toNode) return;
        
        // Pulse effect
        const pulse = Math.sin(timestamp / 500 + conn.from) * 0.5 + 0.5;
        const opacity = conn.active ? 0.2 + pulse * 0.2 : 0.05;
        
        ctx.beginPath();
        ctx.moveTo(fromNode.x, fromNode.y);
        ctx.lineTo(toNode.x, toNode.y);
        ctx.strokeStyle = `rgba(41, 151, 255, ${opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      
      // Add new signals
      if (timestamp - lastSignalTime > 800) {
        const activeConns = connections.filter(() => Math.random() > 0.7);
        activeConns.forEach((conn) => {
          signalsRef.current.push({
            connection: connections.indexOf(conn),
            progress: 0,
          });
        });
        lastSignalTime = timestamp;
      }
      
      // Draw and update signals
      signalsRef.current = signalsRef.current.filter((signal) => {
        const conn = connections[signal.connection];
        if (!conn) return false;
        
        const fromNode = nodes.find((n) => n.id === conn.from);
        const toNode = nodes.find((n) => n.id === conn.to);
        if (!fromNode || !toNode) return false;
        
        signal.progress += 0.02;
        
        if (signal.progress >= 1) return false;
        
        const x = fromNode.x + (toNode.x - fromNode.x) * signal.progress;
        const y = fromNode.y + (toNode.y - fromNode.y) * signal.progress;
        
        // Draw signal
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(41, 151, 255, 0.9)';
        ctx.fill();
        
        // Glow
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, 10);
        gradient.addColorStop(0, 'rgba(41, 151, 255, 0.5)');
        gradient.addColorStop(1, 'rgba(41, 151, 255, 0)');
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        
        return true;
      });
      
      // Draw nodes
      nodes.forEach((node) => {
        const pulse = Math.sin(timestamp / 800 + node.id) * 0.3 + 0.7;
        
        // Outer glow
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, 15);
        gradient.addColorStop(0, `rgba(41, 151, 255, ${0.3 * pulse})`);
        gradient.addColorStop(1, 'rgba(41, 151, 255, 0)');
        ctx.beginPath();
        ctx.arc(node.x, node.y, 15, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Node core
        ctx.beginPath();
        ctx.arc(node.x, node.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(41, 151, 255, ${0.5 + pulse * 0.5})`;
        ctx.fill();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, reducedMotion, isInView]);

  if (reducedMotion) {
    // Static fallback
    const staticLayers = [3, 4, 4, 3];
    const useAdaptiveLayout = width < 350;
    const layerX = useAdaptiveLayout
      ? (() => {
          const horizontalPadding = Math.max(20, Math.round(width * 0.12));
          const usableWidth = Math.max(width - horizontalPadding * 2, 80);
          return staticLayers.map(
            (_count, index) =>
              horizontalPadding + (usableWidth * index) / Math.max(staticLayers.length - 1, 1)
          );
        })()
      : [50, 150, 250, 350];

    return (
      <div ref={wrapperRef} className={`relative ${className}`} style={{ width, height }}>
        <svg width={width} height={height} className="opacity-30">
          <defs>
            <radialGradient id="nodeGradient">
              <stop offset="0%" stopColor="#2997ff" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#2997ff" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Static nodes */}
          {staticLayers.flatMap((count, layerIndex) => {
            const spacing = height / (count + 1);
            return Array.from({ length: count }).map((_, nodeIndex) => (
              <circle
                key={`${layerIndex}-${nodeIndex}`}
                cx={layerX[layerIndex]}
                cy={spacing * (nodeIndex + 1)}
                r="4"
                fill="#2997ff"
                opacity="0.5"
              />
            ));
          })}
        </svg>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Glow background */}
      <div className="absolute inset-0 bg-apple-blue/5 rounded-full blur-3xl" />
      <canvas
        ref={canvasRef}
        className="relative z-10"
        style={{ width, height }}
      />
    </div>
  );
};

export default NeuralNetwork;
